import React, { useState, useMemo, useCallback } from 'react';

import { RowButton } from '@eika/button';
import { Input } from '@eika/input';

import useTranslation, { format } from '../../../../hooks/useTranslation';
import { SelectList } from '../../../shared/SelectList';
import { formatAmountRange } from '../AmountRangeInput';
import FilterButton from '../SearchResetButtons/FilterButton';
import { useTransactionFilterContext } from '../TransactionFilterContext/TransactionFilterContext';
import { formatDateRangeMessage } from '../utils/getSearchDates';
import { FilterSelector } from './FilterSelector';
import MobileAmountRangeModal from './MobileFilterModals/MobileAmountRangeModal';
import MobileCategoryModal from './MobileFilterModals/MobileCategoryModal';
import MobileDateRangeModal from './MobileFilterModals/MobileDateRangeModal';
import { FilterModalType, TransactionModalContentProps } from './modalTypes';

import './ModalStyle.scss';

const MAX_FREETEXT_LENGTH = 32;

const MobileTransactionFilterModalContent: React.FC<TransactionModalContentProps> = ({ targetCurrency }) => {
    const translation = useTranslation();
    const {
        transactionCategory,
        isFreetextSearch,
        freetext,
        dateRange,
        isCategorySearch,
        minAmount,
        maxAmount,
        moneyDirection,
    } = useTransactionFilterContext();

    const [subModalType, setSubModalType] = useState<FilterModalType>(FilterModalType.NONE);
    const categorySubModalIsShowing = subModalType === FilterModalType.CATEGORY;
    const dateRangeSubModalIsShowing = subModalType === FilterModalType.DATERANGE;
    const amountRangeSubModalIsShowing = subModalType === FilterModalType.AMOUNTRANGE;

    const [categoryElementToFocusOnExit, setCategoryElementToFocusOnExit] = useState<HTMLButtonElement | null>(null);
    const [dateRangeElementToFocusOnExit, setDateRangeElementToFocusOnExit] = useState<HTMLButtonElement | null>(null);
    const [amountRangeElementToFocusOnExit, setAmountRangeElementToFocusOnExit] = useState<HTMLButtonElement | null>(
        null,
    );

    const dateRangeMessage = useMemo(() => {
        const { fromDate, toDate } = dateRange;
        return formatDateRangeMessage(fromDate.value, toDate.value, {
            lastMonths: translation.transactionFilter.lastMonths,
            lastMonth: translation.transactionFilter.lastMonth,
        });
    }, [dateRange, translation.transactionFilter.lastMonths, translation.transactionFilter.lastMonth]);

    const amountRangeMessage = useMemo(() => {
        return formatAmountRange(minAmount.value, maxAmount.value, '', targetCurrency);
    }, [minAmount, maxAmount, targetCurrency]);

    const resetAmountRange = useCallback(() => {
        minAmount.reset();
        maxAmount.reset();
        moneyDirection.reset();
    }, [minAmount, maxAmount, moneyDirection]);

    const clickAndAvoidPropagation = (e: React.MouseEvent<HTMLButtonElement>, callback: () => void) => {
        e.stopPropagation();
        callback();
    };

    return (
        <section className="transaction-filter__modal">
            {isFreetextSearch ? (
                <Input
                    className="transaction-filter-form__freetext-input"
                    type="search"
                    placeholder={translation.transactionFilter.freetextPlaceholder}
                    value={freetext.value ?? ''}
                    onChange={(e) => freetext.set(e.target.value)}
                    label={translation.transactionFilter.search}
                    maxLength={MAX_FREETEXT_LENGTH}
                />
            ) : null}

            <SelectList>
                <li>
                    <RowButton
                        onClick={() => setSubModalType(FilterModalType.AMOUNTRANGE)}
                        ref={(el) => setAmountRangeElementToFocusOnExit(el)}
                    >
                        <FilterSelector label={translation.transactionFilter.amount}>
                            {amountRangeMessage ? (
                                <FilterButton
                                    onClick={(e) => clickAndAvoidPropagation(e, resetAmountRange)}
                                    elementToFocusOnExit={amountRangeElementToFocusOnExit}
                                    aria-label={format(translation.transactionFilter.remove, amountRangeMessage)}
                                >
                                    {amountRangeMessage}
                                </FilterButton>
                            ) : null}
                        </FilterSelector>
                    </RowButton>
                </li>
                <li>
                    <RowButton
                        onClick={() => setSubModalType(FilterModalType.DATERANGE)}
                        ref={(el) => setDateRangeElementToFocusOnExit(el)}
                    >
                        <FilterSelector label={translation.transactionFilter.period}>
                            {dateRangeMessage ? (
                                <FilterButton
                                    onClick={(e) =>
                                        clickAndAvoidPropagation(e, () => setSubModalType(FilterModalType.DATERANGE))
                                    }
                                    elementToFocusOnExit={dateRangeElementToFocusOnExit}
                                    aria-label={format(translation.transactionFilter.remove, dateRangeMessage)}
                                >
                                    {dateRangeMessage}
                                </FilterButton>
                            ) : null}
                        </FilterSelector>
                    </RowButton>
                </li>
                {isCategorySearch && (
                    <li>
                        <RowButton
                            ref={(el) => setCategoryElementToFocusOnExit(el)}
                            onClick={() => setSubModalType(FilterModalType.CATEGORY)}
                        >
                            <FilterSelector autoFlowGridOnContent label={translation.transactionFilter.chooseCategory}>
                                {transactionCategory.value.length > 0
                                    ? transactionCategory.value.map((cat) => (
                                          <FilterButton
                                              key={cat}
                                              elementToFocusOnExit={categoryElementToFocusOnExit}
                                              onClick={(e) =>
                                                  clickAndAvoidPropagation(e, () =>
                                                      transactionCategory.removeFromArray(cat),
                                                  )
                                              }
                                              aria-label={format(
                                                  translation.transactionFilter.remove,
                                                  translation.transactionCategories[cat],
                                              )}
                                          >
                                              {translation.transactionCategories[cat]}
                                          </FilterButton>
                                      ))
                                    : null}
                            </FilterSelector>
                        </RowButton>
                    </li>
                )}
            </SelectList>
            {isCategorySearch && (
                <MobileCategoryModal
                    open={categorySubModalIsShowing}
                    onClose={() => setSubModalType(FilterModalType.NONE)}
                />
            )}
            <MobileDateRangeModal
                open={dateRangeSubModalIsShowing}
                onClose={() => setSubModalType(FilterModalType.NONE)}
            />
            <MobileAmountRangeModal
                open={amountRangeSubModalIsShowing}
                onClose={() => setSubModalType(FilterModalType.NONE)}
                targetCurrency={targetCurrency}
            />
        </section>
    );
};

export default MobileTransactionFilterModalContent;
